import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { publicShare } from "@/api";


export const ReadOnlyDocumentViewer = ({
  token,
  documentId,
  documentName,
}) => {
  const [preview, setPreview] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [metadataError, setMetadataError] = useState("");
  const [pageError, setPageError] = useState("");
  const [pageLoading, setPageLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setPreview(null);
    setCurrentPage(1);
    setMetadataError("");
    setPageError("");
    setPageLoading(false);

    publicShare
      .fetchDocumentPreview(token, documentId)
      .then((data) => {
        if (cancelled) return;

        const pageCount = Number(data?.pageCount || 0);

        if (pageCount < 1) {
          setMetadataError(
            "Aucune page disponible pour ce document."
          );
          return;
        }

        setPreview({
          ...data,
          pageCount,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setMetadataError(
            "Aperçu sécurisé indisponible pour ce document."
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, documentId]);

  const currentPageUrl = useMemo(() => {
    if (!preview) return "";

    return publicShare.documentPreviewPageUrl(
      token,
      documentId,
      currentPage
    );
  }, [
    token,
    documentId,
    currentPage,
    preview,
  ]);

  useEffect(() => {
    if (
      !preview ||
      currentPage >= preview.pageCount
    ) {
      return undefined;
    }

    const nextPageUrl =
      publicShare.documentPreviewPageUrl(
        token,
        documentId,
        currentPage + 1
      );

    const nextImage = new Image();
    nextImage.src = nextPageUrl;

    return () => {
      nextImage.src = "";
    };
  }, [
    token,
    documentId,
    currentPage,
    preview,
  ]);

  const changePage = (nextPage) => {
    if (!preview) return;

    const safePage = Math.max(
      1,
      Math.min(preview.pageCount, nextPage)
    );

    if (safePage === currentPage) return;

    setPageError("");
    setPageLoading(true);
    setCurrentPage(safePage);
  };

  if (metadataError) {
    return (
      <div
        className="
          h-full flex items-center justify-center
          bg-[var(--do-bg-solid)] p-6 text-center
        "
        data-testid="readonly-document-error"
      >
        <p className="text-sm font-semibold text-[var(--do-ink)]">
          {metadataError}
        </p>
      </div>
    );
  }

  if (!preview) {
    return (
      <div
        className="
          h-full flex items-center justify-center
          bg-[var(--do-bg-solid)] p-6 text-center
        "
        data-testid="readonly-document-generating"
      >
        <p className="text-sm font-semibold text-[var(--do-ink)]">
          Génération de l’aperçu sécurisé…
        </p>
      </div>
    );
  }

  return (
    <div
      className="
        h-full min-h-0 bg-[var(--do-bg-2)]
        flex flex-col
      "
      data-testid="readonly-document-viewer"
      onContextMenu={(event) => {
        event.preventDefault();
      }}
    >
      <div
        className="
          shrink-0 border-b border-[var(--do-border)]
          bg-[var(--do-bg-solid)] px-3 py-2
          flex items-center justify-between gap-3
        "
      >
        <button
          type="button"
          onClick={() => {
            changePage(currentPage - 1);
          }}
          disabled={currentPage <= 1 || pageLoading}
          className="
            inline-flex min-h-9 items-center justify-center
            rounded-md border border-[var(--do-border)]
            bg-[var(--do-bg-solid)] px-3 text-xs font-semibold
            text-[var(--do-ink)]
            hover:bg-[var(--do-bg-solid)]
            disabled:cursor-not-allowed
            disabled:opacity-40
          "
          data-testid="readonly-document-previous"
        >
          Précédente
        </button>

        <div
          className="
            min-w-0 text-center
            text-xs font-semibold text-[var(--do-muted)]
          "
          data-testid="readonly-document-counter"
        >
          Page {currentPage} sur {preview.pageCount}
        </div>

        <button
          type="button"
          onClick={() => {
            changePage(currentPage + 1);
          }}
          disabled={
            currentPage >= preview.pageCount ||
            pageLoading
          }
          className="
            inline-flex min-h-9 items-center justify-center
            rounded-md border border-[var(--do-border)]
            bg-[var(--do-bg-solid)] px-3 text-xs font-semibold
            text-[var(--do-ink)]
            hover:bg-[var(--do-bg-solid)]
            disabled:cursor-not-allowed
            disabled:opacity-40
          "
          data-testid="readonly-document-next"
        >
          Suivante
        </button>
      </div>

      <div
        className="
          relative flex-1 min-h-0 overflow-auto
          px-2 py-4 sm:px-6
        "
      >
        <div
          className="
            mx-auto max-w-5xl
            overflow-hidden rounded-sm
            border border-[var(--do-border)]
            bg-[var(--do-bg-solid)] shadow-sm
          "
          data-testid={`readonly-document-page-${currentPage}`}
        >
          {pageLoading && (
            <div
              className="
                absolute inset-0 z-10
                flex items-center justify-center
                bg-[var(--do-bg-elev)]
              "
              data-testid="readonly-document-page-loading"
            >
              <p className="text-sm font-semibold text-[var(--do-ink)]">
                Chargement de la page…
              </p>
            </div>
          )}

          {pageError ? (
            <div
              className="
                min-h-[320px]
                flex flex-col items-center justify-center
                gap-3 p-6 text-center
              "
              data-testid="readonly-document-page-error"
            >
              <p className="text-sm font-semibold text-[var(--do-ink)]">
                {pageError}
              </p>

              <button
                type="button"
                onClick={() => {
                  setPageError("");
                  setPageLoading(true);

                  setCurrentPage((page) => page);
                }}
                className="
                  rounded-md border border-[var(--do-border)]
                  bg-[var(--do-bg-solid)] px-3 py-2
                  text-xs font-semibold text-[var(--do-ink)]
                  hover:bg-[var(--do-bg-solid)]
                "
              >
                Réessayer
              </button>
            </div>
          ) : (
            <img
              key={currentPageUrl}
              src={currentPageUrl}
              alt={`${documentName} — page ${currentPage}`}
              className="
                block w-full select-none
              "
              draggable={false}
              loading="eager"
              onLoad={() => {
                setPageLoading(false);
                setPageError("");
              }}
              onError={() => {
                setPageLoading(false);
                setPageError(
                  "Impossible de charger cette page."
                );
              }}
              data-testid="readonly-document-page-image"
            />
          )}
        </div>
      </div>
    </div>
  );
};
